import { defineComponent, ref } from "vue";
import Dialog from "@/components/primitives/dialog/Dialog";
import { openModal } from "@/components/providers/DialogProvider";
import Icon from "@/components/ui/icon/Icon";
import Input from "@/components/ui/input/Input";
import { $t } from "@/services/i18n";
import { useApplyTemplate } from "../composables/useApplyTemplate";
import { useTemplateSelector } from "../composables/useTemplateSelector";
import CategorySidebar from "./CategorySidebar";
import DependencyModal from "./DependencyModal";
import TemplateCard from "./TemplateCard";
import TemplatePreview from "./TemplatePreview";

const TemplateSelector = defineComponent({
  name: "TemplateSelector",
  setup() {
    const {
      templates,
      categories,
      selectedCategory,
      searchQuery,
      selectedTemplate,
      templateCountByCategory,
      selectTemplate,
      selectCategory,
    } = useTemplateSelector();

    const { applyTemplate, isApplying, dependencyState } = useApplyTemplate();

    const showPreview = ref(false);

    async function handleSelectTemplate(id: string | null) {
      if (id === null) {
        // "Começar do Zero" - aplicar direto
        await applyTemplate(null);
        return;
      }

      selectTemplate(id);
      showPreview.value = true;
    }

    async function handleApplyTemplate() {
      if (!selectedTemplate.value) return;
      await applyTemplate(selectedTemplate.value);
    }

    function handleBackToList() {
      showPreview.value = false;
      selectTemplate(null);
    }

    return () => (
      <div class="relative flex h-full flex-col">
        <Dialog.Header>
          <Dialog.Title>
            {showPreview.value && selectedTemplate.value
              ? $t(selectedTemplate.value.nameKey as any)
              : $t("agents:templates.title")}
          </Dialog.Title>
          <Dialog.Description>
            {showPreview.value
              ? $t("agents:templates.preview_description")
              : $t("agents:templates.description")}
          </Dialog.Description>
        </Dialog.Header>

        {/* Container principal - NÃO usar Dialog.Body pois interfere com flex layout */}
        <div class="mt-2 flex h-[calc(100vh-120px)] overflow-hidden border-t">
          {showPreview.value && selectedTemplate.value ? (
            <TemplatePreview
              template={selectedTemplate.value}
              onBack={handleBackToList}
              onApply={handleApplyTemplate}
              isApplying={isApplying.value}
            />
          ) : (
            <>
              {/* Sidebar de categorias */}
              <CategorySidebar
                categories={categories.value}
                selectedCategory={selectedCategory.value}
                templateCounts={templateCountByCategory.value}
                onSelect={selectCategory}
                onStartFromScratch={() => handleSelectTemplate(null)}
              />

              {/* Área principal - flex-col para busca fixa + grid scrollável */}
              <div class="flex flex-1 flex-col overflow-hidden">
                {/* Barra de busca - fixa no topo */}
                <div class="shrink-0 border-b bg-card p-4">
                  <Input
                    v-model={searchQuery.value}
                    placeholder={$t("agents:templates.search_placeholder")}
                    class="w-full"
                  >
                    {{
                      prefix: () => (
                        <Icon icon="lucide:search" class="h-4 w-4 text-muted-foreground" />
                      ),
                    }}
                  </Input>
                </div>

                {/* Grid de templates - scrollável */}
                <div class="flex-1 overflow-y-auto bg-background p-4">
                  <div class="grid grid-cols-2 gap-4 lg:grid-cols-3">
                    {/* Templates filtrados */}
                    {templates.value.map((template) => (
                      <TemplateCard
                        key={template.id}
                        template={template}
                        onClick={() => handleSelectTemplate(template.id)}
                      />
                    ))}
                  </div>

                  {/* Mensagem se nenhum template encontrado */}
                  {templates.value.length === 0 && searchQuery.value && (
                    <div class="mt-8 text-center">
                      <Icon
                        icon="lucide:search-x"
                        class="mx-auto mb-2 h-12 w-12 text-muted-foreground/50"
                      />
                      <p class="text-muted-foreground">{$t("agents:templates.no_results")}</p>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Espaço inferior para o modal não ficar colado */}
        {/* <div class="h-6" /> */}

        {/* Modal de resolução de dependências (overlay) */}
        {dependencyState.isResolving && <DependencyModal state={dependencyState} />}
      </div>
    );
  },
});

/**
 * Abre o modal de seleção de templates
 */
export function openTemplateSelector() {
  openModal(<TemplateSelector />, { size: "screen-xl" });
}

export default TemplateSelector;
